import * as React from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import { Select, MenuItem, FormControl, InputLabel, FormHelperText, Checkbox, ListItemText, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';

const useStyles = makeStyles((theme) => ({
  menuProps: {
    marginTop: 5,
    maxHeight: '300px !important',
    borderRadius: `${theme.borderRadius.large}px !important`,
    boxShadow: `${alpha(theme.palette.primary.main, 0.4)} 0 0 15px !important`,
    backgroundImage: 'none',

    overflowX: 'hidden',
    overflowY: 'auto',
    overscrollBehavior: 'contain',

    '&::-webkit-scrollbar': {
      width: '5px',
      backgroundColor: 'rgba(0,0,0,0)',
    },

    '&::-webkit-scrollbar-track': {
      background: 'rgba(0,0,0,0)',
    },

    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0,0,0,.3)',
      borderRadius: '99rem',
    },

    '& .MuiMenuItem-root.MuiMenuItem-gutters': {
      backgroundColor: theme.palette.background.paper,
      '&:hover': {
        backgroundColor: theme.palette.background.hover,
      },
    },
  },
}));

/**
 * 下拉選單
 * @param {string} name 參數
 * @param {string} label 欄位名稱
 * @param {array} options 資料
 * @param {function} changeData 傳送資料 {name, value}
 * @param {string} value 值
 * @param {boolean} disabled 是否禁用
 * @param {boolean} error 資料是否錯誤
 * @param {boolean} rounded 圓角 true false
 * @param {boolean} multiple 多選模式
 * @param {boolean} isSmall 選項面板是否縮小
 */
export function SelectComp({ name, label, options, changeData, value = '', disabled, error, rounded, helperText, multiple = false, isSmall }) {

  const classes = useStyles({ multiple });
  const theme = useTheme();
  const [val, setVal] = React.useState(multiple ? [] : '');
  const sm = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const onChange = (e) => {
    let v = e.target.value;
    setVal(v);
    changeData({ name: name, value: v }); // 只在用戶實際操作時才調用 changeData
  };

  // 顯示選中項目的名稱（多選模式關閉時）
  const renderValue = (selected) => {
    if (multiple && Array.isArray(selected)) {
      return options
        .filter(option => selected.includes(option.id))
        .map(option => option.name)
        .join(', ');
    }
    return selected;
  };


  // 同步外部 value 到內部 val，但不觸發 changeData
  React.useEffect(() => {
    if (multiple) {
      // 強制轉換為陣列，防止外部傳進來 null/undefined
      const nextVal = Array.isArray(value) ? value : value ? [value] : [];
      setVal(nextVal);
    } else {
      setVal(value ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <>
      {
        !options || options.length === 0
          ? null
          :
          <FormControl fullWidth size='small' error={error}>
            <InputLabel
              id='selectLabel'
              sx={{
                color: (theme) => `${disabled ? theme.palette.text.label : theme.palette.text.oldTextField}`,

                '&[data-shrink="true"]': {
                  color: theme.palette.text.main,
                },

                '&.Mui-error': {
                  color: (theme) => `${disabled ? theme.palette.text.label : theme.palette.text.oldTextField}`,
                },
              }}
            >{label}</InputLabel>
            <Select
              id='select'
              labelId='selectLabel'
              label={label}
              variant='outlined'
              value={val}
              multiple={multiple}
              onChange={onChange}
              renderValue={multiple ? renderValue : undefined}
              MenuProps={{
                classes: {
                  paper: classes.menuProps,
                },
                PaperProps: {
                  sx: {
                    maxHeight: isSmall ? sm? '250px !important':'190px !important' : undefined,
                  }
                }
              }}
              sx={{
                height: 40,
                '& .MuiSelect-select': {
                  fontSize: '16px',
                  color: theme.palette.text.primary,
                  fontWeight: 'normal',
                },
                '& fieldset': {
                  borderColor: (theme) => `${disabled ? `${theme.palette.line.disable} !important` : theme.palette.line.dark}`,
                },
                borderRadius: rounded ? '99em' : null,
              }}
              disabled={disabled}
              inputProps={{ 'aria-label': 'Without label' }}
            >
              {options.map((item, index) => (
                <MenuItem
                  key={`selectComp_${index}_${item.id}`}
                  value={item.id}
                  sx={{
                    fontSize: 16,
                  }}
                >
                  {multiple && (
                    <Checkbox
                      checked={Array.isArray(val) && val.includes(item.id)}
                      className={classes.checkbox}
                      sx={{
                        padding: '4px 8px 4px 0',

                        '&.Mui-checked': {
                          color: theme.palette.primary.main,
                        }
                      }}
                    />
                    )
                  }
                  <ListItemText
                    primary={item.name}
                    className={classes.listItemText}
                    sx={{
                      margin: 0,

                      '& .MuiListItemText-primary': {
                        fontSize: 16,
                      },
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
            {error
              ? <FormHelperText>尚未選取</FormHelperText>
              :
              <>
                {
                  helperText &&
                  <FormHelperText
                    sx={{
                      color: (theme) => theme.palette.text.brand,
                      margin: '4px 0 0 14px',
                      fontWeight: 'bold',
                    }}
                  >
                    {helperText}
                  </FormHelperText>
                }
              </>
            }
          </FormControl>
      }
    </>
  );

};